import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { Plus, Download, Filter, Settings, Search } from 'lucide-react';
import { CostItem, Project, User } from '../types';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { canEditProjectFinance, canViewProjectFinance } from '../utils/projectAccess';
import { APP_FILTER_LABEL_CLASS } from '../constants/formControls';
import { WIDGET_PANEL_CLASS } from '../constants/widgetTheme';
import { toast } from 'sonner';
import SurfaceCard from '../components/SurfaceCard';
import CostSpreadsheet from '../components/CostSpreadsheet';
import { updateProject } from '../store';
import { setProjectCostItems } from '../services/dataService';
import { generateId } from '../utils/idGenerator';
import { assignCostItemCodes, generateNextCostItemCode } from '../utils/costItems';
import { normalizeEntityId } from '../utils/entityIds';

export default function CostsSpreadsheetPage() {
  const { projectId } = useParams();
  const { state, loading, reload } = useApp();
  const { projects } = useOrganizationData(state) as {
    projects: Project[];
    users: User[];
  };

  const [costDrafts, setCostDrafts] = useState<CostItem[]>([]);
  const [projectCostChangeLogs, setProjectCostChangeLogs] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const currentUser = state?.currentUser ?? null;
  const project = projects.find((p) => p.id === projectId);
  const canViewFinance = project ? canViewProjectFinance(currentUser, project) : false;
  const canEditFinance = project ? canEditProjectFinance(currentUser, project) : false;

  useEffect(() => {
    if (!project) return;
    const costs = project.costs || [];
    const sortedCosts = [...costs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setCostDrafts(assignCostItemCodes(sortedCosts));
    setProjectCostChangeLogs(project.metadata?.costChangeLogs || []);
  }, [project]);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  }

  if (!project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  if (!canViewFinance) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Cost and budget information is restricted to project admins or owners.</p>
        </div>
      </div>
    );
  }

  const handleCostDraftChange = (index: number, field: keyof CostItem, value: any) => {
    const next = [...costDrafts];
    next[index] = { ...next[index], [field]: value };
    setCostDrafts(next);
  };

  const handleAddCostItem = () => {
    const newCost: CostItem = {
      id: generateId('cost', projectId || 'unknown'),
      code: generateNextCostItemCode(costDrafts),
      description: '',
      category: '',
      amount: 0,
      order: costDrafts.length,
      references: [],
    };
    setCostDrafts([...costDrafts, newCost]);
  };

  const handleRemoveCostItem = (id: string) => {
    setCostDrafts(costDrafts.filter((c) => c.id !== id));
  };

  const handleMoveCostItem = (fromIndex: number, toIndex: number) => {
    const next = [...costDrafts];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setCostDrafts(next);
  };

  const handleSaveCostBreakdown = async () => {
    if (!project || !currentUser) return;

    const normalizedCostDrafts = costDrafts.map((item, index) => ({
      ...item,
      order: index,
    }));

    const previousCostItems = project.costs || [];
    const now = new Date().toISOString();
    const nextChangeLogs: any[] = [];
    const nextById = new Map(normalizedCostDrafts.map((item) => [item.id, item]));

    const toLine = (item: CostItem) =>
      `${item.id} - ${item.code} - ${item.description} (${item.category}): $${item.amount}`;

    normalizedCostDrafts.forEach((item) => {
      const previous = previousCostItems.find((p) => p.id === item.id);
      if (!previous) {
        nextChangeLogs.push({
          id: generateId('change', project.id),
          entityType: 'project',
          entityId: project.id,
          changeType: 'cost',
          fieldName: 'costs',
          oldValue: 'New',
          newValue: toLine(item),
          changedBy: currentUser.id,
          changedByName: currentUser.name,
          changedAt: now,
        });
        return;
      }

      const changed =
        previous.code !== item.code ||
        previous.description !== item.description ||
        previous.category !== item.category ||
        Number(previous.amount) !== Number(item.amount);

      if (changed) {
        nextChangeLogs.push({
          id: generateId('change', project.id),
          entityType: 'project',
          entityId: project.id,
          changeType: 'cost',
          fieldName: 'costs',
          oldValue: toLine(previous),
          newValue: toLine(item),
          changedBy: currentUser.id,
          changedByName: currentUser.name,
          changedAt: now,
        });
      }
    });

    previousCostItems.forEach((item) => {
      if (!nextById.has(item.id)) {
        nextChangeLogs.push({
          id: generateId('change', project.id),
          entityType: 'project',
          entityId: project.id,
          changeType: 'cost',
          fieldName: 'costs',
          oldValue: toLine(item),
          newValue: 'Removed',
          changedBy: currentUser.id,
          changedByName: currentUser.name,
          changedAt: now,
        });
      }
    });

    const nextMetadata = {
      ...(project.metadata || {}),
      costChangeLogs: [...projectCostChangeLogs, ...nextChangeLogs],
    };

    await setProjectCostItems(project.organizationId, project.id, normalizedCostDrafts);
    await updateProject(project.id, {
      metadata: nextMetadata,
      updatedAt: now,
    });

    setCostDrafts(normalizedCostDrafts);

    if (currentUser.email) {
      await reload(currentUser.email);
    }

    toast.success('Cost breakdown updated');
  };

  const filteredCosts = costDrafts.filter((cost) =>
    searchQuery === ''
      ? true
      : cost.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cost.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cost.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cost.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6">
      <SurfaceCard className="mb-6 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-1">
              Cost Breakdown Spreadsheet
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage and track project costs with drag-drop reordering and reference tracking
            </p>
          </div>
        </div>
      </SurfaceCard>

      {/* Search and Controls */}
      <SurfaceCard className={`mb-6 p-4 ${WIDGET_PANEL_CLASS}`}>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <Search className="w-4 h-4 text-muted-foreground mt-0.5" />
          <input
            type="text"
            placeholder="Search costs by ID, code, description, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 rounded border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {canEditFinance && (
            <div className="flex gap-2">
              <button
                onClick={handleAddCostItem}
                className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm text-foreground hover:bg-accent"
              >
                <Plus className="w-4 h-4" />
                Add Cost
              </button>
            </div>
          )}
        </div>
      </SurfaceCard>

      {/* Cost Spreadsheet */}
      <SurfaceCard className="mb-6 p-6">
        <CostSpreadsheet
          costs={filteredCosts}
          canEdit={canEditFinance}
          onCostChange={handleCostDraftChange}
          onRemoveCost={handleRemoveCostItem}
          onMoveCost={handleMoveCostItem}
          onAddCost={handleAddCostItem}
          onSave={handleSaveCostBreakdown}
          totalBudget={filteredCosts.reduce((sum, cost) => sum + Number(cost.amount || 0), 0)}
        />
      </SurfaceCard>

      {/* Change History */}
      {projectCostChangeLogs.length > 0 && (
        <SurfaceCard className="mb-6 p-6">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Change History</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {[...projectCostChangeLogs].reverse().slice(0, 50).map((log) => (
              <div key={log.id} className="rounded border border-border bg-muted/40 p-3 text-xs">
                <div className="mb-2 text-muted-foreground font-medium">
                  {log.changedByName || log.changedBy} • {new Date(log.changedAt).toLocaleString()}
                </div>
                <div className="grid grid-cols-2 gap-2 text-foreground">
                  <div>
                    <span className="font-medium">Old:</span> {log.oldValue}
                  </div>
                  <div>
                    <span className="font-medium">New:</span> {log.newValue}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
}
